import { useMemo, useEffect } from 'react';
import { Typography, RichTextViewer, TextButton } from 'seagull';
import { Checkmark } from 'seagull/icons';
import { getCoverCCFeesChangeBullet, getCoverCCFeesText, getNetTermsChangeBullet } from "../../../business-logic/amendmentsLogic";
import { useViewLegalTerms } from "../../../components/LegalTermsDropdown/hooks/useViewLegalTerms";
import { useStore, useStoreActions } from "../../../contexts";
import { PracticeManagementSyncFailures } from "../../../integrations/PracticeManagement/PracticeManagementSyncFailures/PracticeManagementSyncFailures";
import { legalTermsModel, relationshipModel } from "../../../models";
import { AgreementDataModel } from "../../../models/AgreementDataModel";
import { selectors } from "../../../store/state-selectors";
import styles from "./AgreementView.module.scss";
import AgreementAmendmentsApprovalMethod from "./components/AgreementAmendmentsApprovalMethod";
import AgreementInvoicesApprovalMethod from "./components/AgreementInvoicesApprovalMethod";
import AmendmentChangesInAgreementView from "./components/AmendmentChangesInAgreementView";
import ChangesSummary from "./components/ChangesSummary";
import { AgreementViewCard, SpecificTerm } from "./components/CommonComponents";
import Header from "./components/Header";
import SignedHeader from "./components/SignedHeader";
import StatementOfWork from "./components/StatementOfWork";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const autoprefix = 'agreement-view-';
export default function AgreementView(props) {
  var _relationship$practic;
  const {
    relationship,
    isVendor,
    refresh
  } = props;
  const {
    extraClause
  } = relationship;
  const {
    buttonPrimary
  } = useStore(selectors.user.branding);
  const qboClasses = useStore(state => state.qboClasses.data);
  const {
    fetchQboClasses
  } = useStoreActions();
  const {
    viewLegalTerms
  } = useViewLegalTerms({
    vendorBusinessId: relationship.vendor.id
  });
  const updatedNetTerms = relationshipModel.updatedNetTerms(relationship);
  const netTermsChangeBullet = updatedNetTerms ? getNetTermsChangeBullet(relationship, updatedNetTerms.value()) : null;
  const updatedIsVendorCoveringCCFee = relationshipModel.updatedIsVendorCoveringCCFee(relationship);
  const agreementData = AgreementDataModel.fromRelationship(relationship);
  const legalTerms = useMemo(() => legalTermsModel.fromRelationshipLegalTerms(relationship.legalTermsDocuments), [relationship.legalTermsDocuments]);
  useEffect(() => {
    fetchQboClasses();
  }, []);
  const coverCCFeesChangeBullet = updatedIsVendorCoveringCCFee ? getCoverCCFeesChangeBullet(relationship, updatedIsVendorCoveringCCFee.value()) : null;
  const effectiveDateTerm = relationship.effectiveDate && /*#__PURE__*/_jsx(SpecificTerm, {
    field: "Effective date of agreement",
    value: relationshipModel.formatEffectiveDate(relationship),
    testId: autoprefix + 'effective-date'
  });
  const termsOfServiceList = useMemo(() => {
    if (!legalTerms || legalTerms.length === 0) return null;
    const getName = doc => {
      var _doc$name;
      return doc === null || doc === void 0 || (_doc$name = doc.name) === null || _doc$name === void 0 ? void 0 : _doc$name.replace(/\.pdf$/i, '');
    };
    const renderButton = (doc, text) => /*#__PURE__*/_jsx(TextButton, {
      testId: "tos-".concat(doc === null || doc === void 0 ? void 0 : doc.id),
      onClick: () => viewLegalTerms(doc, relationship.id),
      children: text
    });
    const value = legalTerms.length === 1 ? renderButton(legalTerms[0], 'View') : legalTerms.map((doc, index, arr) => /*#__PURE__*/_jsxs(_Fragment, {
      children: [renderButton(doc, getName(doc)), index < arr.length - 1 && ', ']
    }));
    return /*#__PURE__*/_jsx(SpecificTerm, {
      testId: autoprefix + 'tos-list',
      field: "Terms of Service",
      value: value
    });
  }, [legalTerms, relationship.id, viewLegalTerms]);
  const termsAcceptedAtSigning = useMemo(() => {
    var _relationship$legalTe;
    const statements = (_relationship$legalTe = relationship.legalTermsDocuments) === null || _relationship$legalTe === void 0 ? void 0 : _relationship$legalTe.reduce((acc, term) => {
      var _term$termsOfService$;
      if (term.termsOfService.type === 'explicitApproval' && (_term$termsOfService$ = term.termsOfService.explicitApproval) !== null && _term$termsOfService$ !== void 0 && _term$termsOfService$.statement) {
        var _term$termsOfService$2;
        acc.push((_term$termsOfService$2 = term.termsOfService.explicitApproval) === null || _term$termsOfService$2 === void 0 ? void 0 : _term$termsOfService$2.statement);
      }
      return acc;
    }, []);
    if (!statements || statements.length === 0) return null;
    return /*#__PURE__*/_jsx(SpecificTerm, {
      testId: autoprefix + 'terms-accepted-at-signing',
      field: "Terms explicitly accepted at signing",
      value: statements === null || statements === void 0 ? void 0 : statements.map((statement, index) => /*#__PURE__*/_jsxs("div", {
        className: styles.termsAcceptanceContainer,
        children: [/*#__PURE__*/_jsx(Checkmark, {}), /*#__PURE__*/_jsx(Typography, {
          className: styles.termsAcceptanceStatement,
          weight: "light",
          children: statement
        }, "termsAcceptence-".concat(index, "-statement"))]
      }, "termsAcceptance-".concat(index, "-container")))
    });
  }, [relationship.legalTermsDocuments]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Header, {
      relationship: relationship,
      onUpdate: refresh
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.main,
      "data-testid": "agreement-view",
      children: [/*#__PURE__*/_jsx(ChangesSummary, {
        isVendor: isVendor,
        relationship: relationship,
        testId: autoprefix + 'changes',
        refresh: refresh
      }), ((_relationship$practic = relationship.practiceManagementSyncFailures) === null || _relationship$practic === void 0 ? void 0 : _relationship$practic.length) && /*#__PURE__*/_jsx(PracticeManagementSyncFailures, {
        relationship: relationship,
        refresh: refresh
      }), /*#__PURE__*/_jsx(SignedHeader, {
        relationship: relationship,
        brandColor: buttonPrimary,
        testPrefix: autoprefix
      }), /*#__PURE__*/_jsx(AgreementViewCard, {
        headTitle: "Services Provided",
        children: /*#__PURE__*/_jsx("div", {
          className: styles.services,
          children: /*#__PURE__*/_jsx(StatementOfWork, {
            services: relationship.services,
            isVendor: isVendor,
            qboClasses: qboClasses,
            serviceDetailsProps: {
              agreementData,
              isVendor,
              relationshipState: {
                type: 'approved',
                effectiveDate: relationshipModel.effectiveDate(relationship)
              }
            }
          })
        })
      }), /*#__PURE__*/_jsx(AgreementViewCard, {
        headTitle: "Agreement terms",
        children: /*#__PURE__*/_jsxs("div", {
          className: styles.specificTerms,
          children: [termsOfServiceList, termsAcceptedAtSigning, effectiveDateTerm, /*#__PURE__*/_jsx(SpecificTerm, {
            field: "Payment terms",
            value: relationshipModel.paymentTermsText(relationship),
            testId: autoprefix + 'net-terms',
            changes: netTermsChangeBullet && /*#__PURE__*/_jsx(AmendmentChangesInAgreementView, {
              disableBorderRadius: true,
              items: [netTermsChangeBullet],
              autoApprovalDate: relationshipModel.updatedNetTermsAutoApprovalDate(relationship),
              testId: autoprefix + 'net-terms-changes'
            })
          }), /*#__PURE__*/_jsx(SpecificTerm, {
            testId: autoprefix + 'cc-fees',
            field: "Credit card fees",
            value: getCoverCCFeesText(relationshipModel.actualIsVendorCoveringCCFee(relationship), relationshipModel.vendorName(relationship), relationshipModel.customerName(relationship)),
            changes: coverCCFeesChangeBullet && /*#__PURE__*/_jsx(AmendmentChangesInAgreementView, {
              disableBorderRadius: true,
              items: [coverCCFeesChangeBullet],
              autoApprovalDate: relationshipModel.updatedIsVendorCoveringCCFeeAutoApprovalDate(relationship),
              testId: autoprefix + 'cc-fees-changes'
            })
          }), /*#__PURE__*/_jsx(AgreementAmendmentsApprovalMethod, {
            relationship: relationship,
            testId: autoprefix
          }), /*#__PURE__*/_jsx(AgreementInvoicesApprovalMethod, {
            relationship: relationship,
            testId: autoprefix
          }), extraClause && /*#__PURE__*/_jsx(SpecificTerm, {
            field: "Notes",
            value: /*#__PURE__*/_jsx(RichTextViewer, {
              testId: 'extra-clause-rich-text-viewer',
              value: extraClause,
              skin: "secondary"
            })
          })]
        })
      })]
    })]
  });
}